import { GET_CLIENT_CURRENCIES, GET_PLANS_AND_LICENSE_PRICES } from '../../_pages/store/edu/gql'
import { GRAPHQL_PAYMENTS_API_URL } from './endpoints'
import { gqlOperation } from './graphql-api'

const PLANS_SESSION_KEY = 'prices-store-key'
const CURRENCIES_SESSION_KEY = 'currencies-store-key'
const FULL_IN_CENTS = 100

const CURRENCY_TEXT_LONG = {
  USD: 'US Dollar',
  EUR: 'Euro',
}

const CURRENCY_TEXT_SHORT = {
  USD: 'USD',
  EUR: 'EUR',
}

const CURRENCY_ICON = {
  USD: '/images/icons/web/filled/24x24/us.flag.svg',
  EUR: '/images/icons/web/filled/24x24/eu.flag.svg',
}

const FORMATTER_PER_CURRENCY = {
  USD: new Intl.NumberFormat(['en'], {
    style: 'currency',
    currency: 'usd',
    maximumFractionDigits: 0,
  }),
  EUR: new Intl.NumberFormat(['en'], {
    style: 'currency',
    currency: 'eur',
    maximumFractionDigits: 0,
  }),
}

const storeInStorage = (key, data) => {
  try {
    window.sessionStorage.setItem(key, JSON.stringify(data))
  } catch (e) {
    // nothing to do
  }
}
const getFromStorage = (key) => {
  const stored = window.sessionStorage.getItem(key)
  if (stored) {
    try {
      return JSON.parse(stored)
    } catch (e) {
      return null
    }
  }

  return null
}

export const fetchPlans = async () => {
  const storedPrices = getFromStorage(PLANS_SESSION_KEY)
  if (storedPrices) {
    return { data: storedPrices, errors: [] }
  }

  try {
    const { data } = await gqlOperation({
      query: GET_PLANS_AND_LICENSE_PRICES,
      endpoint: GRAPHQL_PAYMENTS_API_URL,
    })

    storeInStorage(PLANS_SESSION_KEY, data)
    return { data, errors: [] }
  } catch (e) {
    return { data: null, errors: [e] }
  }
}

export const fetchCurrencies = async (token) => {
  const storedCurrencies = getFromStorage(CURRENCIES_SESSION_KEY)

  if (storedCurrencies && storedCurrencies.token === token) {
    return { data: storedCurrencies, errors: [] }
  }

  try {
    const { data } = await gqlOperation({
      query: GET_CLIENT_CURRENCIES,
      endpoint: GRAPHQL_PAYMENTS_API_URL,
      variables: { token },
    })

    data.token = token
    storeInStorage(CURRENCIES_SESSION_KEY, data)
    return { data, errors: [] }
  } catch (e) {
    return { data: null, errors: [e] }
  }
}

export const getCurrencySet = (currency) => {
  const safeguardedCurrency = String(currency).toUpperCase()
  const icon = CURRENCY_ICON[safeguardedCurrency]
  const formatter = FORMATTER_PER_CURRENCY[safeguardedCurrency]
  const textShort = CURRENCY_TEXT_SHORT[safeguardedCurrency]
  const textLong = CURRENCY_TEXT_LONG[safeguardedCurrency]

  return {
    icon,
    formatter: (valueInCents) => {
      const valueFull = valueInCents / FULL_IN_CENTS
      return Number.isNaN(valueFull) ? '' : formatter.format(valueFull)
    },
    textShort,
    textLong,
    get text() {
      return `${CURRENCY_TEXT_SHORT[safeguardedCurrency]} ${CURRENCY_TEXT_LONG[safeguardedCurrency]}`
    },
  }
}

export const getPlanPriceValueByKey = (prices, key, selectedCurrency) => {
  const { formatter } = getCurrencySet(selectedCurrency)

  switch (key) {
    case 'mac.amount': {
      const price = prices.licensePlan.prices.find(({ currency }) => currency === selectedCurrency)
      return price && formatter(price.amount)
    }

    case 'cloud.yearly.monthlyAmount': {
      const yearlyPlan = prices.availableCloudPlans.find(
        ({ product, type }) => product === 'TEAMS' && type === 'YEARLY'
      )

      const price = yearlyPlan.prices.find(({ currency }) => currency === selectedCurrency)
      return price && formatter(price.monthly)
    }

    case 'cloud.yearly.yearlyAmount': {
      const yearlyPlan = prices.availableCloudPlans.find(
        ({ product, type }) => product === 'TEAMS' && type === 'YEARLY'
      )

      const price = yearlyPlan.prices.find(({ currency }) => currency === selectedCurrency)
      return price && formatter(price.invoice)
    }

    case 'cloud.monthly.amount': {
      const monthlyPlan = prices.availableCloudPlans.find(
        ({ product, type }) => product === 'TEAMS' && type === 'MONTHLY'
      )

      const price = monthlyPlan.prices.find(({ currency }) => currency === selectedCurrency)
      return price && formatter(price.monthly)
    }
    case 'cloud.yearly-monthly-difference-month': {
      const monthlyPlan = prices.availableCloudPlans.find(
        ({ product, type }) => product === 'TEAMS' && type === 'MONTHLY'
      )

      const yearlyPlan = prices.availableCloudPlans.find(
        ({ product, type }) => product === 'TEAMS' && type === 'YEARLY'
      )

      const monthlyPrices = monthlyPlan.prices.find(({ currency }) => currency === selectedCurrency)
      const yearlyPrices = yearlyPlan.prices.find(({ currency }) => currency === selectedCurrency)

      return monthlyPrices && yearlyPrices && formatter(monthlyPrices.monthly - yearlyPrices.monthly)
    }
    case 'cloud.yearly-monthly-difference': {
      const monthlyPlan = prices.availableCloudPlans.find(
        ({ product, type }) => product === 'TEAMS' && type === 'MONTHLY'
      )

      const yearlyPlan = prices.availableCloudPlans.find(
        ({ product, type }) => product === 'TEAMS' && type === 'YEARLY'
      )

      const monthlyPrices = monthlyPlan.prices.find(({ currency }) => currency === selectedCurrency)
      const yearlyPrices = yearlyPlan.prices.find(({ currency }) => currency === selectedCurrency)

      return monthlyPrices && yearlyPrices && formatter((monthlyPrices.monthly - yearlyPrices.monthly) * 12)
    }
    case 'cloud.business.monthlyAmount': {
      const businessPlan = prices.availableCloudPlans.find(({ product }) => product === 'BUSINESS')
      const price = businessPlan.prices.find(({ currency }) => currency === selectedCurrency)

      return price && formatter(price.monthly)
    }

    default:
      return null
  }
}
